import React, { useCallback, useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { getChartManagerInstance } from 'src/managers/chartManager/stateManagement/management';
import { clearExportedChartPayload, updateExportedChartPayload } from 'src/stores/actions/chartExporting';
import getProcessedEchartOption from './echartOptionProcessor';
import { PNGExportationForm } from './form';

// Exporting types
const PNG = 'png';
const CSV = 'csv';
const XLSX = 'xlsx';

export default function ChartExporter({
  shouldExport,
  onUpdateExportingAction,
  exportingOptions,
  questionLabels,
  chartType,
  cardType,
  surveyId,
  analyzeId,
  customerId,
  idPerguntaCruzada,
  idAlternativaCruzada
}) {
  // Redux state
  const dispatch = useDispatch();
  const exportedChartConfig = useSelector(
    (state) => state.chartExporting.exportingConfig
  );

  // Referência do elemento âncora que será utilizado para efetuar o download do gráfico exportado
  const anchorRef = useRef(null);

  const getConfig = useCallback(() => ({
    cardType,
    customerId,
    surveyId,
    analyzeId,
    idPerguntaCruzada,
    idAlternativaCruzada,
  }), [surveyId, analyzeId, customerId, idPerguntaCruzada, idAlternativaCruzada]);

  function exportChartToPNG(echartOption) {
    const processedEchartOption = getProcessedEchartOption(
      echartOption,
      chartType,
      cardType,
      questionLabels,
      exportingOptions.rules
    );

    return processedEchartOption;
  }

  function exportToCSV(options) {
    return options;
  }

  function exportToXLSX(options) {
    return options;
  }

  /**
   * Baseado nas opções de exportação, o echartOption será mapeado para utilizar o serviço de processamento
   * de imagem de acordo com as opções de exportação selecionadas pelo cliente.
   */
  const exportingStrategies = {
    [PNG]: exportChartToPNG,
    [CSV]: exportToCSV,
    [XLSX]: exportToXLSX,
  };

  function getExportStrategy(options, exportingType) {
    return exportingStrategies[exportingType] && exportingStrategies[exportingType](options);
  }

  function getConsistentChartOptions() {
    const manager = getChartManagerInstance(cardType);

    if (!Array.isArray(manager)) {
      return manager.getOptions();
    }

    return manager.map((instance) => instance.getOptions());
  }

  // Ao identificar que o gráfico deve ser exportado, a action
  // que será utilizada para realizar uma requisição ao serviço
  // de processamento do gráfico é chamada
  useEffect(() => {
    if (shouldExport && !exportedChartConfig.data) {
      const { exportingType } = exportingOptions;
      const options = exportingType === PNG ? getConsistentChartOptions()
        : getConfig();

      const processResult = getExportStrategy(options, exportingType);
      if (processResult) {
        updateExportedChartPayload(exportingType, processResult, dispatch);
      }
      onUpdateExportingAction(false);
    }
  },
  // eslint-disable-next-line react-hooks/exhaustive-deps
  [
    shouldExport,
    onUpdateExportingAction,
    exportedChartConfig.data,
    dispatch,
  ]);

  const getDownloadableHref = useCallback((exportType) => {
    if (exportType === CSV) {
      return `data:text/csv;charset=utf-8,${(window.escape && window.escape(exportedChartConfig.data)) || exportedChartConfig.data}`;
    } if (exportType === XLSX) {
      // logic to parse xlsx binary to file
      const blob = new Blob([exportedChartConfig.data]);
      return window.URL.createObjectURL(blob);
    }
  }, [exportedChartConfig.data]);

  const getDownloadFileName = function (exportType) {
    return `download.${exportType}`;
  };

  // Aciona de forma automática o download da imagem exportada
  useEffect(() => {
    if (exportedChartConfig.data && anchorRef.current) {
      // Efetua o download do gráfico exportado
      anchorRef.current.click();
      // Limpa os dados de exportação da ultima ação
      clearExportedChartPayload(dispatch);
    }
  }, [exportedChartConfig.data, anchorRef, dispatch]);

  // Componente de exportação (uma tag âncora "a") que será responsável por realizar a ação de exportar
  // pode ser do tipo download caso seja csv | xlsx ou o tipo link que irá abrir o app analyze-chart-exporter
  // para o caso de exportação em png.
  const ExporterAnchor = function () {
    const { exportingType } = exportingOptions;

    if (exportingType === PNG) {
      return (
        <PNGExportationForm base64Content={exportedChartConfig.data} />
      );
    }

    return (
      <a
        ref={anchorRef}
        href={getDownloadableHref(exportingType)}
        download={getDownloadFileName(exportingType)}
      />
    );
  };

  return (
    <>
      { exportedChartConfig.data && (
        <ExporterAnchor />
      )}
    </>
  );
}
